import * as React from "react";
import { Upload } from "@grandvisionhq/api-client";
import { useRecaptcha } from "@grandvisionhq/forms";
import { useApiConfig, statusSuccess, statusFailed, statusWarning, statusLoading, statusUninit } from "@grandvisionhq/state";
import { uploadStatusLoading, uploadStatusSuccess, uploadStatusIdle, uploadStatusError } from "../../types.js";
import { useUpload } from "../../UploadProvider/UploadProvider/UploadProvider.js";
import { Upload as Upload$1 } from "./Upload.js";
const UploadContainer = ({
  scope,
  onStatusChange,
  uploadStatus,
  referenceId,
  ...rest
}) => {
  const { getToken } = useRecaptcha();
  const { apiUrl } = useApiConfig();
  const { configs } = useUpload();
  const scopeConfig = configs[scope];
  const controllerRef = React.useRef();
  const handleDropSuccess = React.useCallback(
    async (file) => {
      if (!scopeConfig || !statusSuccess(scopeConfig)) {
        return;
      }
      onStatusChange?.(uploadStatusLoading());
      const {
        result: { isRecaptchaTokenRequired, maxFileNameLength }
      } = scopeConfig;
      try {
        controllerRef.current = new AbortController();
        const recaptchaToken = isRecaptchaTokenRequired ? await getToken() : void 0;
        const { token: uploadToken } = await Upload.getUploadToken(
          apiUrl,
          {
            scope,
            referenceId,
            recaptchaToken
          },
          { signal: controllerRef.current?.signal }
        );
        const isFileNameTooLong = file.name.length > maxFileNameLength;
        const { storageId, storageToken } = await Upload.sendFile(
          apiUrl,
          {
            scope,
            referenceId,
            uploadedFile: file,
            uploadToken,
            ...isFileNameTooLong ? { alias: file.name.slice(0, maxFileNameLength) } : {}
          },
          { signal: controllerRef.current?.signal }
        );
        const fileRef = {
          storageId,
          storageToken,
          fileName: file.name
        };
        onStatusChange?.(uploadStatusSuccess(fileRef));
      } catch (error) {
        error.name === "AbortError" ? onStatusChange?.(uploadStatusIdle()) : onStatusChange?.(uploadStatusError(error));
      } finally {
        controllerRef.current = void 0;
      }
    },
    [scopeConfig, onStatusChange, referenceId]
  );
  const handleDropError = React.useCallback(
    (fileRejection) => {
      onStatusChange?.(uploadStatusError(fileRejection));
    },
    [onStatusChange]
  );
  const handleCancel = React.useCallback(() => {
    controllerRef.current?.abort();
  }, []);
  const handleDelete = React.useCallback(() => {
    onStatusChange?.(uploadStatusIdle("FILE_REMOVED"));
  }, [onStatusChange]);
  if (!scopeConfig || statusFailed(scopeConfig) || statusWarning(scopeConfig)) {
    return /* @__PURE__ */ React.createElement("p", { color: "red" }, "Could not fetch config for this upload.");
  }
  if (statusLoading(scopeConfig) || statusUninit(scopeConfig)) {
    return /* @__PURE__ */ React.createElement("p", null, "Fetching config...");
  }
  const { maxFileSizeBytes, allowedFileTypes } = scopeConfig.result;
  return /* @__PURE__ */ React.createElement(
    Upload$1,
    {
      ...rest,
      uploadStatus,
      onFileAccepted: handleDropSuccess,
      onFileRejected: handleDropError,
      onDelete: handleDelete,
      onCancel: handleCancel,
      maximumFileSizeBytes: maxFileSizeBytes,
      allowedFileTypes: allowedFileTypes || []
    }
  );
};
export {
  UploadContainer
};
//# sourceMappingURL=UploadContainer.js.map
